import { ItemStatusSliceType } from "../../../services/redux/slices/itemStatusSlice";
import { OrderTypesEnum, OrderTypesKeysEnum } from "../../enums";
import {
  Franchise,
  FranchiseConfig,
  FranchiseDeliveryConfig,
} from "../../types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFn = (key: string, ...args: any[]) => string;

/**
 * Returns a localized label describing the order type and location context
 * (e.g. "Pick up from", "Dine in at", "Delivering to").
 * Extracted for re-use across Pay and Tip screens.
 */
export const getOrderTypeLocationText = (
  orderType: OrderTypesEnum,
  tFn: TranslateFn,
  locale: string | undefined,
  franchiseConfig: FranchiseConfig | undefined,
  delivery: {
    currentDeliveryConfig?: Pick<
      FranchiseDeliveryConfig,
      "usesMap" | "deliveryType"
    > | null;
    selectedAddress?: unknown | null;
  }
): string => {
  switch (orderType) {
    case OrderTypesEnum.TAKE_OUT:
      return tFn(
        "common:pick-up-from",
        {},
        locale,
        franchiseConfig?.takeOut?.customOrderTypeName,
        franchiseConfig?.takeOut?.localizedCustomOrderTypeName
      );
    case OrderTypesEnum.DINE_IN:
      return tFn(
        "common:dine-in-at",
        {},
        locale,
        franchiseConfig?.dineIn?.customOrderTypeName,
        franchiseConfig?.dineIn?.localizedCustomOrderTypeName
      );
    case OrderTypesEnum.DELIVERY:
    case OrderTypesEnum.DELIVERYV2:
      if (delivery.currentDeliveryConfig?.usesMap) {
        return delivery.selectedAddress
          ? tFn("common:delivering-to")
          : tFn("common:delivering-from");
      }

      if (delivery.currentDeliveryConfig?.deliveryType?.displayName) {
        return tFn(
          `common:${delivery.currentDeliveryConfig.deliveryType.displayName}-at`
        );
      }

      return tFn("common:delivering-from");
    default:
      return tFn("common:pick-up-from");
  }
};

export const getDisabledItemsPerOrderType = (
  itemStatusRedux: ItemStatusSliceType,
  orderTypeQueryParam?: OrderTypesEnum
) => {
  const disabledItemsPerOrderTypePerFranchise =
    itemStatusRedux.disabledItemsPerOrderType;

  let disabledItemsIdsPerCurrentOrderType: number[] = [];
  switch (orderTypeQueryParam) {
    case OrderTypesEnum.DINE_IN:
      disabledItemsIdsPerCurrentOrderType =
        disabledItemsPerOrderTypePerFranchise?.DINE_IN;
      break;

    case OrderTypesEnum.TAKE_OUT:
      disabledItemsIdsPerCurrentOrderType =
        disabledItemsPerOrderTypePerFranchise?.TAKE_OUT;
      break;

    case OrderTypesEnum.DELIVERY:
      disabledItemsIdsPerCurrentOrderType =
        disabledItemsPerOrderTypePerFranchise?.DELIVERY;
      break;

    case OrderTypesEnum.DELIVERYV2:
      disabledItemsIdsPerCurrentOrderType =
        disabledItemsPerOrderTypePerFranchise?.DELIVERYV2;
      break;

    default:
      break;
  }

  return disabledItemsIdsPerCurrentOrderType;
};

export function getOrderTypeKeyStringByValue(
  value?: OrderTypesEnum
): OrderTypesKeysEnum {
  const keyStr = Object.keys(OrderTypesEnum).find(
    (key) => OrderTypesEnum[key as keyof typeof OrderTypesEnum] === value
  ) as keyof typeof OrderTypesEnum;

  return OrderTypesKeysEnum[keyStr];
}

export const isOrderTypeSupported = (
  franchiseConfig: FranchiseConfig,
  orderType: OrderTypesEnum
) => {
  switch (orderType) {
    case OrderTypesEnum.TAKE_OUT:
      return franchiseConfig?.takeOut;
    case OrderTypesEnum.DINE_IN:
      return franchiseConfig?.dineIn;
    default:
      return false;
  }
};

export const getFranchiseOrderType = (
  franchiseConfig: any,
  currentFranchiseDeliveryConfg: FranchiseDeliveryConfig | null
) => {
  return {
    [OrderTypesEnum.DINE_IN.toString()]: franchiseConfig?.dineIn,
    [OrderTypesEnum.TAKE_OUT.toString()]: franchiseConfig?.takeOut,
    [OrderTypesEnum.DELIVERY.toString()]: franchiseConfig?.delivery,
    [OrderTypesEnum.DELIVERYV2.toString()]: currentFranchiseDeliveryConfg,
  };
};

export function getFranchiseAvailableOrderTypes(
  franchiseConfig: Franchise["franchiseConfig"] | undefined
): OrderTypesEnum[] {
  const deliveryOrderTypes = franchiseConfig?.deliveryV2?.map(
    (deliveryOrderType) => deliveryOrderType.deliveryType.displayName
  );

  return [
    ...(franchiseConfig?.dineIn ? [OrderTypesEnum.DINE_IN] : []),
    ...(franchiseConfig?.takeOut ? [OrderTypesEnum.TAKE_OUT] : []),
    ...(deliveryOrderTypes?.length ? [OrderTypesEnum.DELIVERYV2] : []),
  ];
}

export const isOnlyOneOrderTypePerFranchise = (
  franchiseConfig: Franchise["franchiseConfig"] | undefined
) => {
  const orderTypes = getFranchiseAvailableOrderTypes(franchiseConfig);

  return (
    orderTypes.length === 1 &&
    (orderTypes[0] !== OrderTypesEnum.DELIVERYV2 ||
      franchiseConfig?.deliveryV2?.length === 1)
  );
};
